import { forwardRef, type PropsWithChildren, type ReactElement } from 'react';

import { TimeseriesChartLegend } from './components/legend/TimeseriesChartLegend.js';
import { TIMESERIES_CHART_DEFAULT_VARIANT } from './constants.js';
import { TimeseriesRawDataContext } from './context/TimeseriesRawData.context.js';
import { useTimeseriesChartConfig } from './hooks/useTimeseriesChartConfig.js';
import { StoreProvider } from './providers/StoreProvider.js';
import { TimeseriesChartIdProvider } from './providers/TimeseriesChartIdProvider.js';
import { TimeseriesConfigProvider } from './providers/TimeseriesConfigProviders.js';
import { TimeseriesDataProvider } from './providers/TimeseriesDataProvider.js';
import { TimeseriesFilteredDataProvider } from './providers/TimeseriesFilteredDataProvider.js';
import { TimeseriesMinMaxXAxisProvider } from './providers/TimeseriesMinMaxXAxisProvider.js';
import { Annotations } from './slots/annotations/Annotations.js';
import { Marker } from './slots/annotations/Marker.js';
import { Track } from './slots/annotations/Track.js';
import { Legend } from './slots/Legend.js';
import { Pan } from './slots/Pan.js';
import { Select } from './slots/Select.js';
import { Area } from './slots/shapes/Area.js';
import { Band } from './slots/shapes/Band.js';
import { Bar } from './slots/shapes/Bar.js';
import { Line } from './slots/shapes/Line.js';
import { ThresholdIndicator } from './slots/ThresholdIndicator.js';
import { Toolbar } from './slots/Toolbar.js';
import { Tooltip } from './slots/Tooltip.js';
import { XAxis } from './slots/XAxis.js';
import { YAxis } from './slots/YAxis.js';
import { Zoom } from './slots/Zoom.js';
import { TimeseriesChart as TimeseriesChartInternal } from './TimeseriesChart.js';
import { TimeseriesChartProps } from './types/timeseries-props.js';
import { TimeseriesChartRef } from './types/timeseries-ref.js';
import {
  hasSeriesWithDatapoints,
  splitTimeseriesPerVariant,
} from './utils/timeseries.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { ChartLayout } from '../core/components/chart-layout/ChartLayout.js';
import { useAutoLegendRefresh } from '../core/components/chart-layout/hooks/useAutoLegendRefresh.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { OverlayProvider } from '../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { ChartSeriesActionContext } from '../core/components/series-action/ChartSeriesActionContext.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';
import { AnnotationsTooltip } from './slots/annotations/Tooltip.js';
import { TimeseriesChartDownloadCSVSlot } from './slots/DownloadCSV.js';

/**
 * The default max height used for the timeserieschart when no explicit
 * height is given
 */
const TIMESERIES_CHART_DEFAULT_HEIGHT = 300;

/**
 * Component name used for data-dt-component in behavioural tracking
 */
const COMPONENT_NAME_BEHAVIOURAL_TRACKING = 'TimeseriesChart';

/**
 * TimeseriesChart component
 */
const TimeseriesChartRoot = forwardRef<
  TimeseriesChartRef,
  TimeseriesChartProps
>((props, forwardedRef) => {
  const {
    children,
    data,
    gapPolicy,
    pointsDisplay,
    colorPalette,
    truncationMode,
    variant = TIMESERIES_CHART_DEFAULT_VARIANT,
    valueRepresentation,
    height: heightProp,
    width,
    seriesActions,
    onMessage,
    loading = false,
    curve,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;
  const [behaviorTrackingProps] = useBehavioralTrackingProps(props);

  const chartHeight =
    coerceSizeValue(heightProp) || TIMESERIES_CHART_DEFAULT_HEIGHT;
  const dataPerVariant = splitTimeseriesPerVariant(
    data ?? [],
    children,
    variant,
  );
  const config = useTimeseriesChartConfig(children, {
    gapPolicy,
    pointsDisplay,
    valueRepresentation,
    colorPalette,
    truncationMode,
    curve,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
  });

  const { emptyState, errorState } = getChartStatesTemplates(children);

  const { shapePriority, ...rawDataPerVariant } = dataPerVariant;
  const hasSeries = hasSeriesWithDatapoints(dataPerVariant);

  const {
    position: legendPosition,
    onRatioChange,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    ratio,
    initialRatio,
    hidden: isLegendHidden,
  } = config.legend;

  const legendRatio = initialRatio ?? ratio;

  const chartLayoutRef = useAutoLegendRefresh(data);

  return (
    <TimeseriesChartIdProvider>
      <TimeseriesConfigProvider config={config}>
        <TimeseriesRawDataContext.Provider value={rawDataPerVariant}>
          <TimeseriesDataProvider
            data={dataPerVariant}
            valueRepresentation={config.valueRepresentation}
            onMessage={onMessage}
          >
            <TimeseriesMinMaxXAxisProvider>
              <ChartSeriesActionContext.Provider
                value={{
                  chartSeriesActions: seriesActions as (
                    series: unknown,
                    datapoint?: unknown,
                  ) => ReactElement<PropsWithChildren>,
                }}
              >
                <StoreProvider
                  data={dataPerVariant}
                  currentDomain={config.currentZoom}
                  initialDomain={config.initialZoom}
                  infiniteZoom={config.infiniteZoom}
                >
                  <TimeseriesFilteredDataProvider>
                    <OverlayProvider>
                      <TooltipProvider>
                        <ChartLayout
                          ref={chartLayoutRef}
                          chartHeight={chartHeight}
                          chartWidth={width}
                          isEmpty={!hasSeries}
                          emptyState={emptyState}
                          errorState={errorState}
                          showLoader={loading}
                          className={className}
                          style={style}
                          {...behaviorTrackingProps}
                          data-dtrum-mask={dataDtrumMask}
                          data-dtrum-allow={dataDtrumAllow}
                          data-dt-component={
                            COMPONENT_NAME_BEHAVIOURAL_TRACKING
                          }
                        >
                          <ChartLayout.Graph>
                            <TimeseriesChartInternal
                              ref={forwardedRef}
                              config={config}
                            />
                          </ChartLayout.Graph>
                          {!isLegendHidden && (
                            <ChartLayout.Legend
                              position={legendPosition}
                              initialRatio={legendRatio}
                              onResize={onRatioChange}
                            >
                              <TimeseriesChartLegend />
                            </ChartLayout.Legend>
                          )}
                        </ChartLayout>
                      </TooltipProvider>
                    </OverlayProvider>
                  </TimeseriesFilteredDataProvider>
                </StoreProvider>
              </ChartSeriesActionContext.Provider>
            </TimeseriesMinMaxXAxisProvider>
          </TimeseriesDataProvider>
        </TimeseriesRawDataContext.Provider>
      </TimeseriesConfigProvider>
    </TimeseriesChartIdProvider>
  );
});

(
  TimeseriesChartRoot as typeof TimeseriesChartRoot & { displayName: string }
).displayName = 'TimeseriesChartRoot';

/**
 * The `TimeseriesChart` visually represents data in sequential order of time.
 * It's used to observe trends, patterns, and fluctuations in the data over a
 * specific time period.
 * @public
 */
export const TimeseriesChart = Object.assign(TimeseriesChartRoot, {
  Band,
  Legend,
  YAxis,
  XAxis,
  Tooltip,
  Annotations,
  Line,
  Area,
  Bar,
  ThresholdIndicator,
  Select,
  Intent,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  ColorRule,
  Toolbar,
  DownloadCSV: TimeseriesChartDownloadCSVSlot,
  Pan,
  Zoom,
});

/**
 * Annotations Slots
 * @public
 */
export const TimeseriesAnnotations = Object.assign(
  {},
  {
    Marker,
    Track,
    Tooltip: AnnotationsTooltip,
  },
);
